// src/features/data/blacklist-tab.helpers.ts
// IR-1045 P5: pure helpers for the rebuilt Blacklist tab (warming set).
import type { StatusPillVariant } from "@/components/iris";

// Entry types Rotem defined for the warming set (2026-06-15). 'username'/'email'
// folded into 'name'; group_* added; countries come later.
export const TYPE_OPTIONS = ["phone", "name", "platform_id", "group_name", "group_id"];
export const PLATFORM_OPTIONS = ["telegram", "whatsapp", "signal"];

export function typePillVariant(type: string): StatusPillVariant {
  switch (type.toLowerCase()) {
    case "phone":
      return "ok";
    case "name":
    case "platform_id":
      return "idle";
    case "group_name":
    case "group_id":
      return "warn";
    default:
      return "idle";
  }
}

// The status column reflects the avatar's live monitoring state, N/A for
// group-type rows, "not onboarded" when no avatar matches. IR-1257: the backend
// now single-sources this word through the canonical reconcile
// (get_avatar_fleet_statuses) shared with the Avatars tab, so it emits the
// canonical avatar vocabulary — 'connected' (not the legacy 'active'), plus
// new|blocked|cooldown|deleted. This map mirrors the Avatars tab's
// `statusVariant` exactly so the same avatar can't render a different color on
// the two tabs (the FullWreck CONNECTED-vs-DISCONNECTED divergence). 'active' is
// kept as a tolerant alias for the legacy fallback path.
export function statusPillVariant(status: string | null | undefined): StatusPillVariant {
  switch ((status ?? "").toLowerCase()) {
    case "connected":
    case "active":
      return "ok";
    case "stale":
    case "cooldown":
      return "warn";
    case "disconnected":
      return "disconnected";
    case "stopped":
      return "stopped";
    case "blocked":
      return "banned";
    case "new":
    case "deleted":
      return "idle";
    case "n/a":
      return "inactive";
    default:
      return "idle"; // "not onboarded" / unknown
  }
}

// IR-1257 reopen (Rotem 07-24): the Blacklist tab search must match the same
// things the operator can SEE in the row — not just the raw `identifier` column
// the backend filtered on. FullWreck's blacklist identifier is the bare phone
// "17639006732", but the visible Avatar Name ("FullWreck") and "+1 …" phone are
// enriched fields, so a name search returned nothing while the Avatars tab (which
// searches name/phone) worked. This matches the query against every visible
// field, plus a digits-only pass so "+1 763…" matches the bare-digit identifier.
export interface BlacklistSearchable {
  identifier?: string | null;
  masked_identifier?: string | null;
  avatar_name?: string | null;
  phone_number?: string | null;
  platform_native_id?: string | null;
  platform?: string | null;
}

export function matchesBlacklistSearch(
  entry: BlacklistSearchable,
  query: string | null | undefined,
): boolean {
  const q = (query ?? "").trim().toLowerCase();
  if (!q) return true;
  const fields = [
    entry.identifier,
    entry.masked_identifier,
    entry.avatar_name,
    entry.phone_number,
    entry.platform_native_id,
    entry.platform,
  ].filter((f): f is string => !!f);
  if (fields.some((f) => f.toLowerCase().includes(q))) return true;
  // Digits-only pass: a phone typed as "+1 763-900" should still hit the bare
  // "17639006732" identifier / "+1 7639006732" phone. Guarded to ≥4 digits so a
  // stray digit in a text query doesn't over-match.
  const qDigits = q.replace(/\D/g, "");
  if (qDigits.length >= 4) {
    return fields.some((f) => f.replace(/\D/g, "").includes(qDigits));
  }
  return false;
}

// Rows written by the avatar->blacklist auto-sync are OUR OWN scraper accounts.
export const AVATAR_SYNC_ADDED_BY = "avatar_sync";

// True when the entry was auto-synced from an avatar. These rows show the number
// in full (no mask, no toggle) — masking only protects real blacklist targets.
export function isAvatarManaged(entry: { added_by?: string | null }): boolean {
  return entry.added_by === AVATAR_SYNC_ADDED_BY;
}

// IR-1045 P5.1 (Rotem 06-16): phone identifiers render masked (****NNNN); offer a
// reveal (eye) toggle to verify the full number only when the masked form actually
// hides characters (i.e. masked differs from the raw identifier). Avatar-managed
// rows are never masked — they are our own accounts, shown in full.
export function isIdentifierMasked(entry: {
  identifier: string;
  masked_identifier: string;
  added_by?: string | null;
}): boolean {
  if (isAvatarManaged(entry)) return false;
  return entry.masked_identifier !== entry.identifier;
}

// Type + platform are mandatory on add (Rotem); identifier always required.
export function canSubmitEntryForm(form: {
  identifier: string;
  identifier_type: string;
  platform: string;
}): boolean {
  return !!form.identifier.trim() && !!form.identifier_type && !!form.platform;
}
